import { useEffect, useMemo, useState } from "react";
import { getCheckoutStatus } from "../lib/api";
import type { CheckoutStatusResult } from "../lib/types";

interface CheckoutSuccessPageProps {
  navigate: (path: string) => void;
}

type CheckoutSuccessPhase = "processing" | "ready" | "timed_out" | "invalid" | "unavailable";

type CheckoutSuccessViewModel = {
  phase: CheckoutSuccessPhase;
  eyebrow: string;
  heading: string;
  message: string;
  statusLabel?: string;
  orderReference: string;
  returnLabel: string;
  returnPath: string;
  showSupportNote: boolean;
  showTicketLinks: boolean;
  supportText: string;
};

export const CHECKOUT_SUCCESS_POLL_INTERVAL_MS = 2000;
export const CHECKOUT_SUCCESS_POLL_TIMEOUT_MS = 40000;
export const CHECKOUT_SUPPORT_EMAIL = "antix@acsstudios.co";

export function readCheckoutSuccessSessionId(search: string): string | undefined {
  const value = new URLSearchParams(search).get("session_id")?.trim();
  return value ? value : undefined;
}

export function maskBuyerEmail(value?: string): string | undefined {
  if (!value) return undefined;
  const [local, domain] = value.trim().toLowerCase().split("@");
  if (!local || !domain) return undefined;
  if (local.length <= 1) return `${local}***@${domain}`;
  return `${local[0]}***@${domain}`;
}

export function ticketCountLabel(count?: number): string | undefined {
  if (!count || count < 1) return undefined;
  return count === 1 ? "1 ticket" : `${count} tickets`;
}

function deliveredTicketPhrase(count?: number, tense: "sent" | "pending" = "sent"): string {
  const label = ticketCountLabel(count) ?? "tickets";
  if (count === 1) return tense === "sent" ? "1 ticket has" : "1 ticket is";
  return tense === "sent" ? `${label} have` : `${label} are`;
}

export function formatCheckoutEventDate(value?: string): string | undefined {
  if (!value) return undefined;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return undefined;
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit"
  }).format(date);
}

export function getCheckoutStatusBadgeClass(label?: string): string {
  if (!label) return "status-badge status-badge-info";
  if (label === "Tickets emailed") return "status-badge status-badge-success";
  if (label === "Preparing tickets" || label === "Email in progress") return "status-badge status-badge-info";
  return "status-badge status-badge-warning";
}

export function shouldContinueCheckoutPolling(
  phase: CheckoutSuccessPhase,
  status: CheckoutStatusResult | undefined,
  elapsedMs: number
): boolean {
  if (phase === "invalid" || phase === "ready" || phase === "timed_out" || phase === "unavailable") return false;
  if (!status) return elapsedMs < CHECKOUT_SUCCESS_POLL_TIMEOUT_MS;
  if (!status.fulfillmentProcessing) return false;
  return elapsedMs < CHECKOUT_SUCCESS_POLL_TIMEOUT_MS;
}

export function getCheckoutSuccessPhase(
  sessionId: string | undefined,
  status: CheckoutStatusResult | undefined,
  timedOut: boolean,
  unavailable = false
): CheckoutSuccessPhase {
  if (!sessionId) return "invalid";
  if (unavailable) return "unavailable";
  if (timedOut) return "timed_out";
  if (status && !status.fulfillmentProcessing) return "ready";
  return "processing";
}

export function buildCheckoutSuccessViewModel(
  phase: CheckoutSuccessPhase,
  status: CheckoutStatusResult | undefined
): CheckoutSuccessViewModel {
  const maskedEmail = maskBuyerEmail(status?.buyerEmail);
  const eventName = status?.eventName;
  const hasTicketLinks = (status?.ticketLinks?.length ?? 0) > 0;
  const returnPath = status?.eventSlug ? `/e/${status.eventSlug}` : "/tickets";
  const returnLabel = status?.eventSlug ? "Back to event" : "Back to tickets";
  const supportText = "Need help? Email antix@acsstudios.co";

  if (phase === "invalid") {
    return {
      phase,
      eyebrow: "ORDER STATUS",
      heading: "We could not load this order confirmation.",
      message: "If you completed checkout, check your email or contact antix@acsstudios.co.",
      orderReference: "Order confirmation is being prepared",
      returnLabel: "Back to tickets",
      returnPath: "/tickets",
      showSupportNote: true,
      showTicketLinks: false,
      supportText
    };
  }

  if (phase === "unavailable") {
    return {
      phase,
      eyebrow: "ORDER STATUS",
      heading: "We couldn't load your order details right now.",
      message: "If you completed checkout, check your email or contact antix@acsstudios.co.",
      orderReference: "Order details unavailable",
      returnLabel: "Back to tickets",
      returnPath: "/tickets",
      showSupportNote: true,
      showTicketLinks: false,
      supportText
    };
  }

  if (phase === "timed_out") {
    return {
      phase,
      eyebrow: "ORDER CONFIRMED",
      heading: "Thank you for your order",
      message: "Your order was received and is still being processed. Your tickets should arrive by email shortly.",
      statusLabel: "Preparing tickets",
      orderReference: status?.orderId ? `Order ID: ${status.orderId}` : "Order confirmation is being prepared",
      returnLabel,
      returnPath,
      showSupportNote: true,
      showTicketLinks: hasTicketLinks,
      supportText
    };
  }

  if (phase === "processing") {
    return {
      phase,
      eyebrow: "ORDER CONFIRMED",
      heading: "Thank you for your order",
      message: "Your payment was confirmed. We're preparing your tickets now.",
      statusLabel: "Preparing tickets",
      orderReference: status?.orderId ? `Order ID: ${status.orderId}` : "Order confirmation is being prepared",
      returnLabel,
      returnPath,
      showSupportNote: false,
      showTicketLinks: false,
      supportText
    };
  }

  if (status?.ticketDeliveryStatus === "sent") {
    return {
      phase,
      eyebrow: "ORDER CONFIRMED",
      heading: eventName ? `Thank you for buying tickets to ${eventName}` : "Thank you for your order",
      message: `Your order is confirmed and ${deliveredTicketPhrase(status.ticketCount, "sent")} been emailed to ${maskedEmail ?? "your email address"}.`,
      statusLabel: "Tickets emailed",
      orderReference: status?.orderId ? `Order ID: ${status.orderId}` : "Order confirmation is being prepared",
      returnLabel,
      returnPath,
      showSupportNote: false,
      showTicketLinks: hasTicketLinks,
      supportText
    };
  }

  if (status?.ticketDeliveryStatus === "failed" || status?.ticketDeliveryStatus === "not_configured") {
    return {
      phase,
      eyebrow: "ORDER CONFIRMED",
      heading: eventName ? `Thank you for buying tickets to ${eventName}` : "Thank you for your order",
      message: "Your order and tickets are confirmed, but the email may be delayed. You can still access your tickets below.",
      statusLabel: "Tickets ready",
      orderReference: status?.orderId ? `Order ID: ${status.orderId}` : "Order confirmation is being prepared",
      returnLabel,
      returnPath,
      showSupportNote: true,
      showTicketLinks: hasTicketLinks,
      supportText
    };
  }

  return {
    phase,
    eyebrow: "ORDER CONFIRMED",
    heading: eventName ? `Thank you for buying tickets to ${eventName}` : "Thank you for your order",
    message: `Your order is confirmed. ${deliveredTicketPhrase(status?.ticketCount, "pending")} still being emailed to ${maskedEmail ?? "your email address"}.`,
    statusLabel: "Email in progress",
    orderReference: status?.orderId ? `Order ID: ${status.orderId}` : "Order confirmation is being prepared",
    returnLabel,
    returnPath,
    showSupportNote: true,
    showTicketLinks: hasTicketLinks,
    supportText
  };
}

export function CheckoutSuccessCard({
  navigate,
  phase,
  status
}: {
  navigate: (path: string) => void;
  phase: CheckoutSuccessPhase;
  status?: CheckoutStatusResult;
}) {
  const model = buildCheckoutSuccessViewModel(phase, status);
  const summaryRows = [
    { label: "Order", value: model.orderReference.replace(/^Order ID:\s*/, "") },
    { label: "Tickets", value: ticketCountLabel(status?.ticketCount) },
    { label: "Event date", value: formatCheckoutEventDate(status?.eventStartsAt) },
    { label: "Venue", value: status?.venueName }
  ].filter((row): row is { label: string; value: string } => Boolean(row.value));

  const viewTicketsLabel =
    (status?.ticketLinks?.length ?? 0) > 1 ? "View my tickets" : "View my ticket";

  return (
    <section className="page marketplace-page marketplace-page-detail checkout-confirm-page">
      <div className="checkout-confirm-shell">
        <article className="checkout-confirm-card">
          <div className="checkout-confirm-copy">
            <p className="checkout-confirm-eyebrow">{model.eyebrow}</p>
            <h1>{model.heading}</h1>
            <p className="checkout-confirm-message">{model.message}</p>
          </div>

          {model.statusLabel && (
            <div className="checkout-confirm-status">
              <span>Delivery status</span>
              <strong className={getCheckoutStatusBadgeClass(model.statusLabel)}>{model.statusLabel}</strong>
            </div>
          )}

          <div className="checkout-confirm-summary">
            <div className="checkout-confirm-summary__row">
              <span>Order ID</span>
              <strong>{status?.orderId ?? "Order confirmation is being prepared"}</strong>
            </div>
            {summaryRows
              .filter((row) => row.label !== "Order")
              .map((row) => (
                <div className="checkout-confirm-summary__row" key={row.label}>
                  <span>{row.label}</span>
                  <strong>{row.value}</strong>
                </div>
              ))}
          </div>

          <div className="button-row checkout-confirm-actions">
            {model.showTicketLinks &&
              status?.ticketLinks?.map((ticket, index) => (
                <a
                  key={ticket.id}
                  className={index === 0 ? "primary-action checkout-confirm-action" : "secondary-action checkout-confirm-action"}
                  href={ticket.url}
                >
                  {(status.ticketLinks?.length ?? 0) > 1 ? `View ticket ${index + 1}` : viewTicketsLabel}
                </a>
              ))}
            <button className="secondary-action checkout-confirm-action" type="button" onClick={() => navigate(model.returnPath)}>
              {model.returnLabel}
            </button>
          </div>

          <div className="checkout-confirm-support">
            <p>
              <a href={`mailto:${CHECKOUT_SUPPORT_EMAIL}`}>{model.supportText}</a>
            </p>
            {model.showSupportNote && <p>Please include your order ID when contacting support.</p>}
          </div>
        </article>
      </div>
    </section>
  );
}

export default function CheckoutSuccessPage({ navigate }: CheckoutSuccessPageProps) {
  const sessionId = useMemo(() => readCheckoutSuccessSessionId(window.location.search), []);
  const [status, setStatus] = useState<CheckoutStatusResult>();
  const [timedOut, setTimedOut] = useState(false);
  const [invalid, setInvalid] = useState(!sessionId);
  const [unavailable, setUnavailable] = useState(false);

  useEffect(() => {
    if (!sessionId) {
      setInvalid(true);
      return;
    }

    setUnavailable(false);

    let cancelled = false;
    let intervalId: ReturnType<typeof setInterval> | undefined;
    const startedAt = Date.now();

    const stopPolling = () => {
      if (intervalId) clearInterval(intervalId);
    };

    const poll = async () => {
      try {
        const nextStatus = await getCheckoutStatus(sessionId);
        if (cancelled) return;
        if (!nextStatus) {
          setInvalid(true);
          stopPolling();
          return;
        }

        setStatus(nextStatus);
        setInvalid(false);

        const elapsedMs = Date.now() - startedAt;
        if (!shouldContinueCheckoutPolling("processing", nextStatus, elapsedMs)) {
          stopPolling();
        }
      } catch {
        if (cancelled) return;
        setUnavailable(true);
        stopPolling();
      }
    };

    void poll();
    intervalId = setInterval(() => {
      const elapsedMs = Date.now() - startedAt;
      if (!shouldContinueCheckoutPolling("processing", status, elapsedMs)) {
        if (elapsedMs >= CHECKOUT_SUCCESS_POLL_TIMEOUT_MS) setTimedOut(true);
        stopPolling();
        return;
      }
      void poll();
    }, CHECKOUT_SUCCESS_POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      stopPolling();
    };
  }, [sessionId]);

  const phase = invalid ? "invalid" : getCheckoutSuccessPhase(sessionId, status, timedOut, unavailable);
  return <CheckoutSuccessCard navigate={navigate} phase={phase} status={status} />;
}
